/**
 * Placement.js
 * 
 * This module handles the placement logic for solar panels in the Eagle Eye Designer application.
 * It provides functionality for:
 * 1. Validating panel placement within segments
 * 2. Detecting overlaps between panels and obstacles
 * 3. Creating grid-snapping behavior for precise panel alignment
 * 4. Generating visual grid guides for the user
 * 
 * The module uses THREE.js for raycasting and geometry operations to ensure
 * accurate placement in 3D space.
 */

import * as THREE from 'three';
import { POLYGONS } from "../Global/variables";
import { getVertices } from './Helpers';
import { earClipping, createSegmentPlane } from '../Global/functions';
import { sceneManager } from '../SetUps/SceneManager';

/**
 * Standard offset between panels in meters
 * Converted from inches (1 inch = 0.0254 meters, so 1/39.37 is approximately 0.0254)
 */
const OFFSET = 1/39.37;

/**
 * Threshold for snapping in world units
 * Determines how close objects need to be to snap together
 */
let snapThreshold = 0.15;

/**
 * Checks if a panel is properly placed within a segment
 * 
 * This function validates that:
 * 1. All panel vertices are within the setback boundaries of the segment
 * 2. The panel doesn't overlap with other panels
 * 3. The panel doesn't overlap with obstacles
 * 
 * @param {THREE.Mesh} panel - The panel mesh to check
 * @param {number} index - The index of the segment in the POLYGONS array
 * @returns {boolean} True if the panel is properly placed, false otherwise
 */
export function panelInSegment(panel, index) {
  // Create a plane representing the segment surface
  const plane = createSegmentPlane(index);
  
  // Create a temporary mesh representing the setback area
  const mesh = setbackSegment(index);
  
  // Initialize raycaster for intersection tests
  const raycaster = new THREE.Raycaster();
  
  // Get the normal vector of the segment plane (perpendicular to the surface)
  const normal = plane.normal.clone().normalize();

  // Get all vertices of the panel
  const vertices = getVertices(panel);

  // Check if each vertex of the panel is within the setback boundaries
  const projected = vertices.map(vertex => {
    // Cast a ray from the vertex in the direction of the normal      
    raycaster.set(vertex, normal);
    const intersects = raycaster.intersectObject(mesh, false);
    
    // Cast another ray in the opposite direction (handles orientation differences)
    raycaster.set(vertex, normal.negate());
    const intersects2 = raycaster.intersectObject(mesh, false);
    
    // If either ray intersects with the mesh, the vertex is within the segment
    if (intersects.length > 0 || intersects2.length > 0){
      return true;
    }
    return false;
  });
  
  // Clean up the temporary mesh
  removeMesh(mesh);

  // Check three conditions:
  // 1. All vertices are within the setback boundary
  // 2. The panel doesn't overlap with other panels
  // 3. The panel doesn't overlap with obstacles
  return projected.every(point => point) && 
         !panelOverlap(raycaster, normal, [...vertices, panel.position.clone()], index, panel) && 
         !obstacleOverlap(raycaster, normal, index, panel);
}

/**
 * Creates a temporary mesh representing the setback area of a segment
 * 
 * @param {number} index - The index of the segment in the POLYGONS array
 * @returns {THREE.Mesh} The created mesh
 */
const setbackSegment = (index) => {
  // Get the vertices from the setback edges
  const vertices = POLYGONS[index].setBacks.edgesData.map((edge) => edge.point1);
  
  // Create a mesh using the ear clipping triangulation algorithm
  const mesh = earClipping(vertices);
  
  // Add the mesh to the scene (temporarily)
  sceneManager.scene.add(mesh);
  
  return mesh;
}

/**
 * Removes a mesh from the scene and cleans up its resources
 * 
 * @param {THREE.Mesh} mesh - The mesh to remove
 */
const removeMesh = (mesh) => {
  sceneManager.scene.remove(mesh);
  mesh.geometry.dispose();
  mesh.material.dispose();
}

/**
 * Checks if a panel overlaps with other panels in the segment
 * 
 * @param {THREE.Raycaster} raycaster - Initialized raycaster for intersection tests
 * @param {THREE.Vector3} normal - Normal vector of the segment plane
 * @param {Array<THREE.Vector3>} vertices - Vertices of the panel to check
 * @param {number} index - The index of the segment in the POLYGONS array
 * @param {THREE.Mesh} panel - The panel mesh to check
 * @returns {boolean} True if there is an overlap, false otherwise
 */
const panelOverlap = (raycaster, normal, vertices, index, panel) => {  
  // Get all existing panels in the segment (cloned to avoid reference issues)
  const existingPanels = POLYGONS[index].panels.map(panel => panel.panel.clone());
  
  // If the panel we're checking is already in the array, remove it to avoid self-intersection
  const cut = POLYGONS[index].panels.map(pan => pan.panel).indexOf(panel);
  if (cut > -1) {
    existingPanels.splice(cut, 1);
  }
  
  // Check if any vertex of the current panel intersects with existing panels
  for (let i=0; i < vertices.length; i++) {
    // Position the ray slightly below the vertex in the direction opposite to the normal
    const point = vertices[i].clone().add(normal.clone().multiplyScalar(-2));
    raycaster.set(point, normal);
    const intersects = raycaster.intersectObjects(existingPanels, false);    
    if (intersects.length > 0) return true; // Overlap detected
  }
  
  // Check if any vertex of existing panels intersects with the current panel
  for (let p of existingPanels) {
    const pves = getVertices(p);
    pves.push(p.position.clone());
    for (let j=0; j < pves.length; j++) {
      const point = pves[j].clone().add(normal.clone().multiplyScalar(-2));
      raycaster.set(point, normal);
      const intersects = raycaster.intersectObject(panel, false);
      if (intersects.length > 0) return true; // Overlap detected
    }
  }
  
  return false; // No overlaps found
}

/**
 * Checks if a panel overlaps with obstacles in the segment
 * 
 * @param {THREE.Raycaster} raycaster - Initialized raycaster for intersection tests
 * @param {THREE.Vector3} normal - Normal vector of the segment plane
 * @param {number} index - The index of the segment in the POLYGONS array
 * @param {THREE.Mesh} panel - The panel mesh to check
 * @returns {boolean} True if there is an overlap, false otherwise
 */
const obstacleOverlap = (raycaster, normal, index, panel) => {
  // Get all obstacles in the segment
  const obstructions = POLYGONS[index].obstructions;
  
  // Get the offset shapes of the obstacles
  const shapes = obstructions.map((obs) => {return obs.mesh.getObjectByName('offset')});
  
  // Get all vertices of all obstacles
  const points = shapes.map((shape) => {return getVertices(shape)});
  
  // Check if any obstacle vertex intersects with the panel
  for (let point of points) {
    for (let p=0; p < point.length; p++) {
      raycaster.set(point[p], normal.negate());
      const intersects = raycaster.intersectObject(panel, false);
      if (intersects.length > 0) return true; // Overlap detected
    }
  }
  
  return false; // No overlaps found
}

/**
 * Creates a grid helper for panel placement and calculates snap positions
 * 
 * This function finds the best panel to align with, generates a grid, and
 * calculates where the current panel should snap to.
 * 
 * @param {THREE.Mesh} panel - The panel being placed
 * @param {Array} panels - Array of existing panels in the segment
 * @param {THREE.Vector3} mouse - Current mouse position
 * @returns {Array} An array containing [gridMesh, snappedPosition, bestPanelToSnapTo]
 */
export function gridHelper(panel, panels, mouse) {
  // If there are no existing panels, no grid is needed
  if (panels.length === 0) return [null, null, null];

  // Find the best panel to align with
  const bestPanel = findBestPanel(panel, panels);

  // If no suitable panel was found, return null values
  if (!bestPanel.panel) return [null, null, null];

  // Get the panel's position in world and local (best panel) coordinates
  const panelWorldPos = panel.position.clone();
  const panelLocalPos = bestPanel.panel.worldToLocal(panelWorldPos.clone());

  // Calculate the snapped position
  const snappedWorldPos = snapToGrid(panel, bestPanel.panel, bestPanel.cellW, bestPanel.cellH, mouse);
  const snappedLocalPos = bestPanel.panel.worldToLocal(snappedWorldPos.clone());
  const snapDeltaTolerance = 0.001;

  // Adjust threshold based on panel type (portrait or landscape)
  const baseThreshold = 0.1; // Equal threshold for movement
  const snapThresholdX = panel.panelType === 'portrait' ? 
    baseThreshold * (panel.geometry.parameters.width / panel.geometry.parameters.height) : 
    baseThreshold;
  const snapThresholdY = panel.panelType === 'landscape' ? 
    baseThreshold * (panel.geometry.parameters.height / panel.geometry.parameters.width) : 
    baseThreshold;

  // Determine if horizontal and vertical snapping are active
  const horizontalSnap = Math.abs(snappedLocalPos.x - panelLocalPos.x) > snapDeltaTolerance;
  const verticalSnap = Math.abs(snappedLocalPos.y - panelLocalPos.y) > snapDeltaTolerance;

  const activateColumn = horizontalSnap;
  const activateRow = verticalSnap;

  // Apply snapping if within threshold
  if (bestPanel.dx <= snapThresholdX) panel.position.x = bestPanel.col * bestPanel.cellW;
  if (bestPanel.dy <= snapThresholdY) panel.position.y = bestPanel.row * bestPanel.cellH;

  // Calculate grid lines and create grid geometry
  let positions = gridCalculations(activateColumn, activateRow, bestPanel);
  let grid = gridGeometry(positions);

  return [grid, snappedWorldPos, bestPanel.panel];
}

/**
 * Finds the best panel to align with based on proximity and alignment
 * 
 * This function analyzes all existing panels to find which one is most
 * suitable for alignment with the current panel.
 * 
 * @param {THREE.Mesh} panel - The panel being placed
 * @param {Array} panels - Array of existing panels to check against
 * @returns {Object|null} The best panel candidate with metadata or null if none found
 */
function findBestPanel(panel, panels) {
  if (!panels.length) return null;

  let bestCandidate = null;
  let bestMetric = Infinity;

  // Iterate over all candidate panels
  for (let cand of panels) {
    // Skip self-comparison
    if (cand.panel.id === panel.id) continue;

    const candPanel = cand.panel;
    const { width, height } = candPanel.geometry.parameters;
    
    // Calculate cell dimensions (panel size plus offset)
    const cellW = width + OFFSET;
    const cellH = height + OFFSET;

    // Get world positions and convert the moving panel's position into candidate–local space
    const worldCand = new THREE.Vector3();
    const worldPanel = new THREE.Vector3();
    candPanel.getWorldPosition(worldCand);
    panel.getWorldPosition(worldPanel);

    // Calculate relative position in candidate's local space
    const relPos = new THREE.Vector3().subVectors(worldPanel, worldCand);
    const invQuat = candPanel.quaternion.clone().invert();
    relPos.applyQuaternion(invQuat);

    // Determine the candidate's grid cell indices
    const col = Math.round(relPos.x / cellW);
    const row = Math.round(relPos.y / cellH);
    const gridCenterX = col * cellW;
    const gridCenterY = row * cellH;

    // Compute candidate grid boundaries
    const candidateLeft = gridCenterX - cellW / 2;
    const candidateRight = gridCenterX + cellW / 2;
    const candidateBottom = gridCenterY - cellH / 2;
    const candidateTop = gridCenterY + cellH / 2;

    // Get the moving panel's half dimensions
    const panelWidth = panel.geometry.parameters.width;
    const panelHeight = panel.geometry.parameters.height;
    const panelHalfWidth = panelWidth / 2;
    const panelHalfHeight = panelHeight / 2;

    // Compute the moving panel's edges in candidate local space
    const panelLeft = relPos.x - panelHalfWidth;
    const panelRight = relPos.x + panelHalfWidth;
    const panelBottom = relPos.y - panelHalfHeight;
    const panelTop = relPos.y + panelHalfHeight;

    // Calculate the differences between the moving panel's edges and candidate grid boundaries
    const diffLeft = Math.abs(panelLeft - candidateLeft);
    const diffRight = Math.abs(panelRight - candidateRight);
    const diffTop = Math.abs(panelTop - candidateTop);
    const diffBottom = Math.abs(panelBottom - candidateBottom);

    // For snapping purposes we care about both horizontal and vertical misalignment
    const horizontalMetric = Math.min(diffLeft, diffRight);
    const verticalMetric = Math.min(diffTop, diffBottom);
    const compositeMetric = horizontalMetric + verticalMetric;

    // Calculate dx and dy for exact position snapping
    const dx = Math.abs(relPos.x - gridCenterX);
    const dy = Math.abs(relPos.y - gridCenterY);

    // Choose the candidate with the smallest overall misalignment
    if (compositeMetric < bestMetric) {
      bestMetric = compositeMetric;
      bestCandidate = {
        panel: candPanel,
        metric: compositeMetric,
        horizontalMetric: horizontalMetric,
        verticalMetric: verticalMetric,
        col: col,
        row: row,
        cellW: cellW,
        cellH: cellH
      };
    }
  }
  return bestCandidate;
}

/**
 * Calculates the positions for grid lines based on the best panel
 * 
 * @param {boolean} activateColumn - Whether to show vertical grid lines
 * @param {boolean} activateRow - Whether to show horizontal grid lines
 * @param {Object} bestPanel - The best panel candidate with metadata
 * @returns {Array} Array of positions for grid line vertices
 */
function gridCalculations(activateColumn, activateRow, bestPanel) {
  // Calculate the range of grid lines to show
  const minCol = Math.min(0, bestPanel.col) - 1;
  const maxCol = Math.max(0, bestPanel.col) + 1;
  const minRow = Math.min(0, bestPanel.row) - 1;
  const maxRow = Math.max(0, bestPanel.row) + 1;

  const positions = [];
  const halfCellX = bestPanel.cellW / 2;
  const halfCellY = bestPanel.cellH / 2;

  // Add vertical grid lines if activated
  if (activateColumn) {
    for (let i = minCol; i <= maxCol; i++) {
      const x = i * bestPanel.cellW + halfCellX;
      positions.push(x, minRow * bestPanel.cellH + halfCellY, 0);
      positions.push(x, maxRow * bestPanel.cellH + halfCellY, 0);
    }
  }

  // Add horizontal grid lines if activated
  if (activateRow) {
    for (let j = minRow; j <= maxRow; j++) {
      const y = j * bestPanel.cellH + halfCellY;
      positions.push(minCol * bestPanel.cellW + halfCellX, y, 0);
      positions.push(maxCol * bestPanel.cellW + halfCellX, y, 0);
    }
  }
  return positions;
}

/**
 * Creates a THREE.js line segments geometry for the grid
 * 
 * @param {Array} positions - Array of positions for grid line vertices
 * @returns {THREE.LineSegments} The created grid mesh
 */
function gridGeometry(positions) {
  // Create buffer geometry from positions
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3)
  );

  // Create dashed line material for the grid
  const material = new THREE.LineDashedMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.5,
    dashSize: 0.3,
    gapSize: 0.2,
    depthTest: false  // Grid is always visible even if behind objects
  });

  // Create line segments with the geometry and material
  const grid = new THREE.LineSegments(geometry, material);
  grid.renderOrder = 4;  // Set higher render order to ensure visibility
  grid.name = "grid";
  grid.computeLineDistances();  // Required for dashed lines
  return grid;
}

/**
 * Calculates the snapped position for a panel based on the grid
 * 
 * This function determines where a panel should snap to when placed near
 * other panels, handling both same-type and different-type panel snapping.
 * 
 * @param {THREE.Mesh} panel - The panel being placed
 * @param {THREE.Mesh} candidatePanel - The panel to snap to
 * @param {number} cellWidth - Width of a grid cell
 * @param {number} cellHeight - Height of a grid cell
 * @param {THREE.Vector3} mouse - Current mouse position
 * @returns {THREE.Vector3} The calculated snapped position in world coordinates
 */
function snapToGrid(panel, candidatePanel, cellWidth, cellHeight, mouse) {
  // Convert panel position to candidate's local coordinate system
  const panelWorldPos = panel.position.clone();
  const panelLocalPos = candidatePanel.worldToLocal(panelWorldPos.clone());

  // Dynamically adjust threshold based on panel size
  const panelWidth = panel.geometry.parameters.width;
  const panelHeight = panel.geometry.parameters.height;
  const maxDimension = Math.max(panelWidth, panelHeight); // Use longest side for scaling

  const baseThreshold = 0.1; // Original threshold factor
  const snapThreshold = (baseThreshold / maxDimension) * Math.max(cellWidth, cellHeight);

  // Start with current position
  let newLocalX = panelLocalPos.x;
  let newLocalY = panelLocalPos.y;

  // Calculate grid cell indices and center
  const colIndex = Math.round(panelLocalPos.x / cellWidth);
  const rowIndex = Math.round(panelLocalPos.y / cellHeight);
  const gridCenterX = colIndex * cellWidth;
  const gridCenterY = rowIndex * cellHeight;

  // Different snapping logic based on whether panels are the same type
  if (panel.panelType === candidatePanel.panelType) {
    // === Horizontal Snapping (X axis) ===
    // Snap to center of grid cell
    if (Math.abs(panelLocalPos.x - gridCenterX) < snapThreshold * cellWidth) {
      newLocalX = gridCenterX;
    } else {
      // Snap to left or right edge of grid cell
      const leftSnapX = gridCenterX - cellWidth / 2;
      const rightSnapX = gridCenterX + cellWidth / 2;
      if (Math.abs(panelLocalPos.x - leftSnapX) < snapThreshold * cellWidth) {
        newLocalX = leftSnapX;
      } else if (Math.abs(panelLocalPos.x - rightSnapX) < snapThreshold * cellWidth) {
        newLocalX = rightSnapX;
      }
    }

    // === Vertical Snapping (Y axis) ===
    // Snap to center of grid cell
    if (Math.abs(panelLocalPos.y - gridCenterY) < snapThreshold * cellHeight) {
      newLocalY = gridCenterY;
    } else {
      // Snap to top or bottom edge of grid cell
      const bottomSnapY = gridCenterY - cellHeight / 2;
      const topSnapY = gridCenterY + cellHeight / 2;
      if (Math.abs(panelLocalPos.y - bottomSnapY) < snapThreshold * cellHeight) {
        newLocalY = bottomSnapY;
      } else if (Math.abs(panelLocalPos.y - topSnapY) < snapThreshold * cellHeight) {
        newLocalY = topSnapY;
      }
    }
  } else {
    // === Side Snapping (Different Type) ===
    // When panels are different types (portrait vs landscape), snap edges to edges
    
    const bestWidth = candidatePanel.geometry.parameters.width;
    const bestHeight = candidatePanel.geometry.parameters.height;

    const bestColIndex = Math.round(panelLocalPos.x / cellWidth);
    const bestRowIndex = Math.round(panelLocalPos.y / cellHeight);
    const bestGridX = bestColIndex * cellWidth;
    const bestGridY = bestRowIndex * cellHeight;

    // Calculate edges of the panels for snapping
    const bestLeft = bestGridX - bestWidth / 2;
    const bestRight = bestGridX + bestWidth / 2;
    const bestTop = bestGridY + bestHeight / 2;
    const bestBottom = bestGridY - bestHeight / 2;

    const panelLeft = panelLocalPos.x - panelWidth / 2;
    const panelRight = panelLocalPos.x + panelWidth / 2;
    const panelTop = panelLocalPos.y + panelHeight / 2;
    const panelBottom = panelLocalPos.y - panelHeight / 2;

    // Horizontal edge snapping logic
    if (Math.abs(panelLeft - bestLeft) <= snapThreshold * cellWidth) {
      newLocalX = bestLeft + panelWidth / 2; // Snap left edge
      console.log("snap 1");
      
    } else if (Math.abs(panelRight - bestRight) <= snapThreshold * cellWidth) {
      console.log("snap 2");
      newLocalX = bestRight - panelWidth / 2; // Snap right edge
    } 
    else if (Math.abs(panelLeft - bestGridX) <= snapThreshold * cellWidth) {
      console.log("snap 3");
      newLocalX = bestGridX + panelWidth / 2; // Snap left to grid
    } 
    else if (Math.abs(panelRight - bestGridX) <= snapThreshold * cellWidth) {
      console.log("snap 4");
      newLocalX = bestGridX - panelWidth / 2; // Snap right to grid
    }

    // Vertical edge snapping logic
    if (Math.abs(panelTop - bestTop) <= snapThreshold * cellHeight) {
      newLocalY = bestTop - panelHeight / 2; // Snap top edge
      console.log("snap 5");
    }
      if (Math.abs(panelBottom - bestBottom) <= snapThreshold * cellHeight) {
      newLocalY = bestBottom + panelHeight / 2; // Snap bottom edge
      console.log("snap 6");
    } 
    else if (Math.abs(panelTop - bestGridY) <= snapThreshold * cellHeight) {
      newLocalY = bestGridY - panelHeight / 2; // Snap top to grid
      console.log("snap 7");
    }
     else if (Math.abs(panelBottom - bestGridY) <= snapThreshold * cellHeight) {
      newLocalY = bestGridY + panelHeight / 2; // Snap bottom to grid
      console.log("snap 8");
    }
  }

  // Create new position vector and convert back to world coordinates
  const newLocalPos = new THREE.Vector3(newLocalX, newLocalY, panelLocalPos.z);
  return candidatePanel.localToWorld(newLocalPos);
}